import localforage from 'localforage'
import { branchesAPI, type BranchOption } from 'api/branches'
import { diningHallsAPI } from 'api/diningHalls'
import { diningTablesAPI } from 'api/diningTables'
import { expensesAPI } from 'api/expenses'
import { settingsAPI } from 'api/settings'
import { shiftsAPI } from 'api/shifts'
import { syncAPI } from 'api/sync'
import { getUserData } from 'helpers/Auth'
import { getQueue, type QueuedMutation } from 'store/offlineQueue'
import * as posStore from 'store/posStore'
import * as tableOps from 'store/tableOpsQueue'
import { checkConnectivity } from 'utils/connectivityCheck'

export type OfflineBootstrapRunStatus = 'idle' | 'running' | 'success' | 'partial_failed' | 'failed'
export type OfflineBootstrapModuleRunStatus = 'idle' | 'running' | 'success' | 'failed' | 'skipped'

export interface OfflineBootstrapModuleStatus {
 status: OfflineBootstrapModuleRunStatus
 count?: number
 total?: number
 error?: string
 last_synced_at?: string | null
 stale?: boolean
 branch_id?: string | null
 user_id?: string | number | null
 cache_key?: string
}

/** Counts for sync attention — each bucket is disjoint (no double-counting). */
export interface PendingActionsSnapshot {
 /** Redux mutation queue: pending / syncing only (excludes failed + conflicts). */
 pending_mutations: number
 /** Redux mutation queue rows that exhausted retries (status = failed). */
 failed_mutations: number
 /** POS outbox orders not in conflict state. */
 pending_pos_orders: number
 /** Dead-letter failed POS orders (see pos_failed_orders). */
 failed_orders: number
 mutation_conflicts: number
 pos_order_conflicts: number
 /** Pending table draft/settle/transfer ops (pos_table_ops_queue). */
 pending_table_ops: number
 /** Failed table ops (pos_failed_table_ops). */
 failed_table_ops: number
 table_op_conflicts: number
}

export interface OfflineBootstrapStatus {
 status: OfflineBootstrapRunStatus
 started_at: string | null
 finished_at: string | null
 last_success_at: string | null
 tenant_id: string | null
 branch_id: string | null
 user_id: string | number | null
 reason?: string
 message?: string
 modules: Record<string, OfflineBootstrapModuleStatus>
 pending_actions: PendingActionsSnapshot
}

function emptyPendingActions(): PendingActionsSnapshot {
 return {
 pending_mutations: 0,
 failed_mutations: 0,
 pending_pos_orders: 0,
 failed_orders: 0,
 mutation_conflicts: 0,
 pos_order_conflicts: 0,
 pending_table_ops: 0,
 failed_table_ops: 0,
 table_op_conflicts: 0,
 }
}

export function buildPendingActionsSnapshot(
 queue: QueuedMutation[],
 pendingOrders: posStore.PendingOfflineOrder[],
 failedOrders: posStore.FailedOfflineOrder[],
 tableOpsQueue: tableOps.TableOp[] = [],
 failedTableOps: tableOps.FailedTableOp[] = [],
): PendingActionsSnapshot {
 const mutationConflicts = queue.filter((m) => m.status === 'conflict').length
 const failedMutations = queue.filter((m) => m.status === 'failed').length
 const pendingMutations = queue.filter(
 (m) => m.status === 'pending' || m.status === 'syncing' || m.status == null,
 ).length
 const posOrderConflicts = pendingOrders.filter((o) => o.status === 'conflict').length
 const pendingPosOrders = pendingOrders.filter((o) => o.status !== 'conflict').length
 const tableOpConflicts =
 tableOpsQueue.filter((o) => o.status === 'conflict').length +
 failedTableOps.filter((f) => f.op.status === 'conflict').length
 const pendingTableOps = tableOpsQueue.filter(
 (o) => o.status === 'pending' || o.status === 'syncing',
 ).length
 return {
 pending_mutations: pendingMutations,
 failed_mutations: failedMutations,
 pending_pos_orders: pendingPosOrders,
 failed_orders: failedOrders.length,
 mutation_conflicts: mutationConflicts,
 pos_order_conflicts: posOrderConflicts,
 pending_table_ops: pendingTableOps,
 failed_table_ops: failedTableOps.length,
 table_op_conflicts: tableOpConflicts,
 }
}

export function attentionTotalFromPendingActions(pa: PendingActionsSnapshot): number {
 return (
 pa.pending_mutations +
 pa.failed_mutations +
 pa.pending_pos_orders +
 pa.failed_orders +
 pa.mutation_conflicts +
 pa.pos_order_conflicts +
 pa.pending_table_ops +
 pa.failed_table_ops +
 pa.table_op_conflicts
 )
}

export async function getSyncAttentionSnapshot(): Promise<PendingActionsSnapshot> {
 const [queue, pendingOrders, failedOrders, tableOpsQueue, failedTableOps] = await Promise.all([
 getQueue(),
 posStore.getPendingOrders(),
 posStore.getFailedOrders(),
 tableOps.getTableOpsQueue(),
 tableOps.getFailedTableOps(),
 ])
 return buildPendingActionsSnapshot(queue, pendingOrders, failedOrders, tableOpsQueue, failedTableOps)
}

type CachedOfflineData<T = unknown> = {
 data: T
 tenant_id: string | null
 branch_id: string | null
 user_id: string | number | null
 last_synced_at: string
 data_version?: string | number | null
}

type Scope = Required<posStore.OfflineCacheScope> & {
 token?: string | null
 permissions: string[]
 roles: string[]
 isSuperAdmin: boolean
 branchIds: string[]
 canUseGlobalView: boolean
}

const bootstrapStore = localforage.createInstance({
 name: 'erp-offline',
 storeName: 'bootstrap',
})

const STATUS_KEY = 'offline_bootstrap_status'
const UNKNOWN_SCOPE = 'unknown'
const STALE_AFTER_MS = 24 * 60 * 60 * 1000
const FRESH_RUN_MS = 5 * 60 * 1000

let inFlight: Promise<OfflineBootstrapStatus> | null = null
const listeners = new Set<(status: OfflineBootstrapStatus) => void>()

function scopePart(value: string | number | null | undefined): string {
 const raw = value == null ? '' : String(value).trim()
 return encodeURIComponent(raw || UNKNOWN_SCOPE)
}

function readScope(): Scope | null {
 const user = getUserData()
 if (!user?.token) return null

 let stored: any = null
 if (typeof localStorage !== 'undefined') {
 const raw = localStorage.getItem('authUser')
 if (raw) {
 try {
 stored = JSON.parse(raw)
 } catch {
 stored = null
 }
 }
 }

 const tenantId =
 stored?.tenant_slug ??
 (typeof localStorage !== 'undefined' ? localStorage.getItem('tenant_slug') : null) ??
 (user as any).tenant_id ??
 (user as any).company_id ??
 UNKNOWN_SCOPE
 const branchViewMode = typeof localStorage !== 'undefined' ? localStorage.getItem('branch_view_mode') : null
 const selectedBranchId = typeof localStorage !== 'undefined' ? localStorage.getItem('selected_branch_id') : null
 const branchId =
 branchViewMode === 'global'
 ? UNKNOWN_SCOPE
 : selectedBranchId ?? user.current_branch_id ?? UNKNOWN_SCOPE

 return {
 tenantId,
 branchId,
 userId: user.id ?? UNKNOWN_SCOPE,
 token: user.token,
 permissions: user.permissions ?? [],
 roles: user.roles ?? (user.role ? [user.role] : []),
 isSuperAdmin: Boolean(
 user.is_super_admin ||
 user.roles?.some((r) => r === 'owner' || r === 'Super Admin')
 ),
 branchIds: user.branch_ids ?? [],
 canUseGlobalView: Boolean(user.can_use_global_view || user.has_global_view),
 }
}

function dataKey(
 moduleName: string,
 scope: Pick<Scope, 'tenantId' | 'branchId' | 'userId'>,
 mode: 'branch' | 'user' | 'userBranch' | 'tenant' = 'branch',
): string {
 const parts = ['offline:data', moduleName, scopePart(scope.tenantId)]
 if (mode === 'user' || mode === 'userBranch') parts.push(scopePart(scope.userId))
 if (mode === 'branch' || mode === 'userBranch') parts.push(scopePart(scope.branchId))
 return parts.join(':')
}

async function cacheData<T>(
 moduleName: string,
 scope: Pick<Scope, 'tenantId' | 'branchId' | 'userId'>,
 data: T,
 mode: 'branch' | 'user' | 'userBranch' | 'tenant' = 'branch',
 dataVersion?: string | number | null,
): Promise<string> {
 const now = new Date().toISOString()
 const key = dataKey(moduleName, scope, mode)
 const payload: CachedOfflineData<T> = {
 data,
 tenant_id: scope.tenantId,
 branch_id: mode === 'tenant' || mode === 'user' ? null : scope.branchId,
 user_id: mode === 'tenant' || mode === 'branch' ? null : scope.userId,
 last_synced_at: now,
 data_version: dataVersion ?? null,
 }
 await bootstrapStore.setItem(key, payload)
 return key
}

function isStale(lastSyncedAt?: string | null): boolean {
 if (!lastSyncedAt) return true
 const t = Date.parse(lastSyncedAt)
 if (!Number.isFinite(t)) return true
 return Date.now() - t > STALE_AFTER_MS
}

function emit(status: OfflineBootstrapStatus): void {
 listeners.forEach((listener) => {
 try {
 listener(status)
 } catch {
 /* ignore listener failures */
 }
 })
 if (typeof window !== 'undefined') {
 window.dispatchEvent(new CustomEvent('offline-bootstrap:updated', { detail: status }))
 }
}

async function persist(status: OfflineBootstrapStatus): Promise<void> {
 Object.values(status.modules).forEach((moduleStatus) => {
 moduleStatus.stale = moduleStatus.status === 'success' ? isStale(moduleStatus.last_synced_at) : moduleStatus.stale
 })
 await bootstrapStore.setItem(STATUS_KEY, status)
 emit(status)
}

function errorMessage(error: unknown): string {
 if (error && typeof error === 'object') {
 const anyErr = error as { response?: { data?: { message?: string } }; message?: string }
 return anyErr.response?.data?.message || anyErr.message || 'Unknown error'
 }
 return typeof error === 'string' ? error : 'Unknown error'
}

function isPermissionDenied(error: unknown): boolean {
 if (error && typeof error === 'object') {
 const anyErr = error as { response?: { status?: number } }
 return anyErr.response?.status === 403
 }
 return false
}

export { formatBootstrapModuleLabel } from './offlineBootstrapLabels'

function countArrayLike(value: unknown): number {
 if (Array.isArray(value)) return value.length
 if (!value || typeof value !== 'object') return 0
 const rec = value as Record<string, unknown>
 const candidates = [
 rec.data,
 rec.items,
 rec.branches,
 rec.tables,
 rec.users,
 (rec.data as any)?.data,
 (rec.data as any)?.branches,
 (rec.data as any)?.tables,
 (rec.data as any)?.users,
 ]
 for (const item of candidates) {
 if (Array.isArray(item)) return item.length
 }
 return 0
}

function can(permissionScope: Scope, permission: string): boolean {
 return permissionScope.isSuperAdmin || permissionScope.permissions.includes(permission)
}

function canCacheExpenses(scope: Scope): boolean {
 return can(scope, 'manage_expenses') || can(scope, 'approve_expenses') || can(scope, 'process_sales') || scope.roles.includes('cashier')
}

function canCacheUsers(scope: Scope): boolean {
 return can(scope, 'manage_users') || scope.roles.includes('admin') || scope.roles.includes('Super Admin')
}

function extractBranches(payload: Awaited<ReturnType<typeof branchesAPI.listAccessible>> | null): BranchOption[] {
 return payload?.data?.branches ?? []
}

function resolveTargetBranches(scope: Scope, accessibleBranches: BranchOption[]): BranchOption[] {
 const byId = new Map<string, BranchOption>()
 accessibleBranches.forEach((branch) => byId.set(String(branch.id), branch))

 for (const id of scope.branchIds) {
 if (!byId.has(String(id))) {
 byId.set(String(id), { id: String(id), name: String(id), code: String(id), is_main: false })
 }
 }

 if (scope.branchId && scope.branchId !== UNKNOWN_SCOPE && !byId.has(String(scope.branchId))) {
 byId.set(String(scope.branchId), { id: String(scope.branchId), name: String(scope.branchId), code: String(scope.branchId), is_main: false })
 }

 const list = Array.from(byId.values())
 if (!scope.canUseGlobalView && scope.branchId && scope.branchId !== UNKNOWN_SCOPE) {
 return list.filter((branch) => String(branch.id) === String(scope.branchId))
 }
 return list
}

async function updatePendingCounts(status: OfflineBootstrapStatus): Promise<void> {
 const [queue, pendingOrders, failedOrders, tableOpsQueue, failedTableOps] = await Promise.all([
 getQueue(),
 posStore.getPendingOrders(),
 posStore.getFailedOrders(),
 tableOps.getTableOpsQueue(),
 tableOps.getFailedTableOps(),
 ])
 status.pending_actions = buildPendingActionsSnapshot(
 queue,
 pendingOrders,
 failedOrders,
 tableOpsQueue,
 failedTableOps,
 )
}

async function runModule(
 status: OfflineBootstrapStatus,
 moduleId: string,
 branchId: string | null,
 task: () => Promise<{ count?: number; total?: number; cacheKey?: string; skipped?: boolean }>,
 opts?: { nonCritical?: boolean },
): Promise<void> {
 status.modules[moduleId] = {
 status: 'running',
 branch_id: branchId,
 user_id: status.user_id,
 last_synced_at: null,
 }
 await persist(status)

 try {
 const result = await task()
 const now = new Date().toISOString()
 status.modules[moduleId] = {
 status: result.skipped ? 'skipped' : 'success',
 count: result.count,
 total: result.total,
 branch_id: branchId,
 user_id: status.user_id,
 cache_key: result.cacheKey,
 last_synced_at: result.skipped ? null : now,
 stale: false,
 }
 } catch (error) {
 const denied = opts?.nonCritical && isPermissionDenied(error)
 status.modules[moduleId] = {
 status: denied ? 'skipped' : 'failed',
 branch_id: branchId,
 user_id: status.user_id,
 error: errorMessage(error),
 last_synced_at: null,
 stale: !denied,
 }
 }
 await updatePendingCounts(status)
 await persist(status)
}

function summarizeStatus(status: OfflineBootstrapStatus): OfflineBootstrapRunStatus {
 const modules = Object.values(status.modules).filter((m) => m.status !== 'skipped')
 if (modules.length === 0) return 'failed'
 const failures = modules.filter((m) => m.status === 'failed')
 const successes = modules.filter((m) => m.status === 'success')
 if (failures.length === 0 && successes.length > 0) return 'success'
 if (successes.length > 0) return 'partial_failed'
 return 'failed'
}

export async function getOfflineBootstrapStatus(): Promise<OfflineBootstrapStatus | null> {
 const status = await bootstrapStore.getItem<OfflineBootstrapStatus>(STATUS_KEY)
 if (!status) return null
 Object.values(status.modules).forEach((moduleStatus) => {
 if (moduleStatus.status === 'success') moduleStatus.stale = isStale(moduleStatus.last_synced_at)
 })
 if (!status.pending_actions || typeof (status.pending_actions as { mutation_queue?: number }).mutation_queue === 'number') {
 status.pending_actions = emptyPendingActions()
 }
 await updatePendingCounts(status)
 return status
}

export async function getOfflineCachedData<T = unknown>(
 moduleName: string,
 scopeOverride: Partial<Pick<Scope, 'tenantId' | 'branchId' | 'userId'>> = {},
 mode: 'branch' | 'user' | 'userBranch' | 'tenant' = 'branch',
): Promise<CachedOfflineData<T> | null> {
 const scope = readScope()
 if (!scope) return null
 const key = dataKey(moduleName, { ...scope, ...scopeOverride }, mode)
 return bootstrapStore.getItem<CachedOfflineData<T>>(key)
}

export function subscribeOfflineBootstrap(listener: (status: OfflineBootstrapStatus) => void): () => void {
 listeners.add(listener)
 void getOfflineBootstrapStatus().then((status) => {
 if (status) listener(status)
 }).catch(() => {})
 return () => {
 listeners.delete(listener)
 }
}

async function runBootstrap(opts: { force?: boolean; reason?: string } = {}): Promise<OfflineBootstrapStatus> {
 const scope = readScope()
 const previous = await getOfflineBootstrapStatus()
 if (!scope) {
 const status: OfflineBootstrapStatus = {
 status: 'idle',
 started_at: null,
 finished_at: null,
 last_success_at: previous?.last_success_at ?? null,
 tenant_id: null,
 branch_id: null,
 user_id: null,
 reason: opts.reason,
 message: 'No authenticated user',
 modules: {},
 pending_actions: emptyPendingActions(),
 }
 await persist(status)
 return status
 }

 if (
 !opts.force &&
 previous &&
 (previous.status === 'success' || previous.status === 'partial_failed') &&
 previous.tenant_id === scope.tenantId &&
 String(previous.user_id ?? '') === String(scope.userId ?? '') &&
 (previous.branch_id ?? UNKNOWN_SCOPE) === (scope.branchId === UNKNOWN_SCOPE ? UNKNOWN_SCOPE : scope.branchId) &&
 previous.last_success_at &&
 Date.now() - Date.parse(previous.last_success_at) < FRESH_RUN_MS
 ) {
 await updatePendingCounts(previous)
 await persist(previous)
 return previous
 }

 const startedAt = new Date().toISOString()
 const status: OfflineBootstrapStatus = {
 status: 'running',
 started_at: startedAt,
 finished_at: null,
 last_success_at: previous?.last_success_at ?? null,
 tenant_id: scope.tenantId,
 branch_id: scope.branchId === UNKNOWN_SCOPE ? null : scope.branchId,
 user_id: scope.userId,
 reason: opts.reason,
 modules: {},
 pending_actions: emptyPendingActions(),
 }

 await updatePendingCounts(status)
 await persist(status)

 if (!(await checkConnectivity())) {
 status.modules.connectivity = {
 status: 'failed',
 error: 'Offline: connect to the internet and run sync first.',
 last_synced_at: null,
 stale: true,
 branch_id: status.branch_id,
 user_id: status.user_id,
 }
 status.status = 'failed'
 status.finished_at = new Date().toISOString()
 status.message = 'لم يتم تجهيز هذه البيانات للعمل بدون إنترنت. اتصل بالإنترنت وقم بالمزامنة أولًا.'
 await updatePendingCounts(status)
 await persist(status)
 return status
 }

 let accessibleBranches: BranchOption[] = []
 await runModule(status, 'branches', null, async () => {
 const res = await branchesAPI.listAccessible()
 accessibleBranches = extractBranches(res)
 const cacheKey = await cacheData('branches', scope, accessibleBranches, 'user')
 return { count: accessibleBranches.length, total: accessibleBranches.length, cacheKey }
 })

 await runModule(status, 'permissions', null, async () => {
 const permissionsSnapshot = {
 user: {
 id: scope.userId,
 current_branch_id: scope.branchId === UNKNOWN_SCOPE ? null : scope.branchId,
 },
 roles: scope.roles,
 permissions: scope.permissions,
 is_super_admin: scope.isSuperAdmin,
 branch_ids: scope.branchIds,
 }
 const cacheKey = await cacheData('permissions', scope, permissionsSnapshot, 'user', scope.permissions.length)
 return { count: scope.permissions.length + scope.roles.length, cacheKey }
 })

 const targetBranches = resolveTargetBranches(scope, accessibleBranches)
 if (targetBranches.length === 0) {
 status.modules.pos_catalog = {
 status: 'skipped',
 error: 'No branch context is available for POS bootstrap.',
 branch_id: null,
 user_id: status.user_id,
 last_synced_at: null,
 }
 await persist(status)
 }

 for (const branch of targetBranches) {
 const branchScope: Scope = { ...scope, branchId: String(branch.id) }
 await runModule(status, `pos_catalog:${branch.id}`, String(branch.id), async () => {
 const res = await syncAPI.pullFullPosData(String(branch.id))
 if (res.status !== 'success' || !res.data) throw new Error(res.message || 'Failed to pull POS data')
 await posStore.setCatalog(res.data, branchScope)
 const cacheKey = await cacheData('pos_catalog', branchScope, res.data, 'branch', res.data.version)
 if (typeof window !== 'undefined') window.dispatchEvent(new Event('pos:catalog-updated'))
 const count =
 (res.data.products?.length ?? 0) +
 (res.data.categories?.length ?? 0) +
 (res.data.customers?.length ?? 0) +
 (res.data.vaults?.length ?? 0) +
 (res.data.warehouses?.length ?? 0)
 return { count, cacheKey }
 })

 await runModule(status, `active_shift:${branch.id}`, String(branch.id), async () => {
 const res = await shiftsAPI.current(String(branch.id))
 if (res.status !== 'success') throw new Error(res.message || 'Failed to pull active shift')
 await posStore.setCachedActiveShift(res.data ?? null, branchScope)
 const cacheKey = await cacheData('active_shift', branchScope, res.data ?? null, 'userBranch')
 return { count: res.data ? 1 : 0, cacheKey }
 })

 await runModule(status, `tables:${branch.id}`, String(branch.id), async () => {
 const [halls, tables] = await Promise.all([
 diningHallsAPI.listForPos(String(branch.id)),
 diningTablesAPI.listForPos(String(branch.id)),
 ])
 const payload = {
 halls: halls.data ?? [],
 tables: tables.data?.tables ?? [],
 status_counts: tables.data?.status_counts ?? null,
 }
 const cacheKey = await cacheData('tables', branchScope, payload, 'branch')
 return { count: payload.halls.length + payload.tables.length, cacheKey }
 }, { nonCritical: true })

 await runModule(status, `expense_categories:${branch.id}`, String(branch.id), async () => {
 if (!canCacheExpenses(scope)) return { skipped: true }
 const res = await expensesAPI.getCategories({ branch_id: String(branch.id), per_page: 100, page: 1 })
 const cacheKey = await cacheData('expense_categories', branchScope, res, 'branch')
 return { count: countArrayLike(res), cacheKey }
 }, { nonCritical: true })
 }

 await runModule(status, 'cashier_users', null, async () => {
 if (!canCacheUsers(scope)) return { skipped: true }
 const res = await settingsAPI.getUsers({ per_page: 100, page: 1 })
 const cacheKey = await cacheData('cashier_users', scope, res, 'user')
 return { count: countArrayLike(res), cacheKey }
 })

 await runModule(status, 'pending_actions', null, async () => {
 await updatePendingCounts(status)
 const cacheKey = await cacheData('pending_actions', scope, status.pending_actions, 'userBranch')
 return {
 count: attentionTotalFromPendingActions(status.pending_actions),
 cacheKey,
 }
 })

 status.status = summarizeStatus(status)
 status.finished_at = new Date().toISOString()
 if (status.status === 'success' || status.status === 'partial_failed') {
 status.last_success_at = status.finished_at
 }
 status.message =
 status.status === 'success'
 ? 'تم تجهيز النظام للعمل بدون إنترنت'
 : status.status === 'partial_failed'
 ? 'تم تجهيز بعض البيانات، وهناك بيانات لم تكتمل'
 : 'فشل تجهيز البيانات للعمل بدون إنترنت'
 await updatePendingCounts(status)
 await persist(status)
 return status
}

export function runOfflineBootstrap(opts: { force?: boolean; reason?: string } = {}): Promise<OfflineBootstrapStatus> {
 if (inFlight) return inFlight
 inFlight = runBootstrap(opts).finally(() => {
 inFlight = null
 })
 return inFlight
}

export interface ManualOfflineSyncOutcome {
 bootstrap: OfflineBootstrapStatus
 replay: { replayed: number; errors: string[] }
 posPush: { pushed: number; errors: string[] } | null
 skippedOffline: boolean
 replayThrewMessage: string | null
 posPushThrewMessage: string | null
}

export async function flushOfflineBootstrapPendingCounts(): Promise<OfflineBootstrapStatus | null> {
 const s = await bootstrapStore.getItem<OfflineBootstrapStatus>(STATUS_KEY)
 if (!s) return null
 if (
 !s.pending_actions ||
 typeof (s.pending_actions as { mutation_queue?: number }).mutation_queue === 'number'
 ) {
 s.pending_actions = emptyPendingActions()
 }
 await updatePendingCounts(s)
 await persist(s)
 return s
}

export async function runManualOfflineSyncNow(): Promise<ManualOfflineSyncOutcome> {
 const emptyReplay = (): ManualOfflineSyncOutcome['replay'] => ({ replayed: 0, errors: [] as string[] })

 const online = await checkConnectivity()
 if (!online) {
 const status = await getOfflineBootstrapStatus()
 if (status) {
 status.status = 'failed'
 status.message = 'لا يوجد اتصال بالإنترنت حاليًا'
 status.finished_at = new Date().toISOString()
 status.reason = 'manual-sync'
 await updatePendingCounts(status)
 await persist(status)
 return {
 bootstrap: status,
 replay: emptyReplay(),
 posPush: null,
 skippedOffline: true,
 replayThrewMessage: null,
 posPushThrewMessage: null,
 }
 }
 const scope = readScope()
 const fallback: OfflineBootstrapStatus = {
 status: 'failed',
 started_at: null,
 finished_at: new Date().toISOString(),
 last_success_at: null,
 tenant_id: scope?.tenantId ?? null,
 branch_id: scope?.branchId === UNKNOWN_SCOPE ? null : scope?.branchId ?? null,
 user_id: scope?.userId ?? null,
 reason: 'manual-sync',
 message: 'لا يوجد اتصال بالإنترنت حاليًا',
 modules: {},
 pending_actions: emptyPendingActions(),
 }
 await updatePendingCounts(fallback)
 return {
 bootstrap: fallback,
 replay: emptyReplay(),
 posPush: null,
 skippedOffline: true,
 replayThrewMessage: null,
 posPushThrewMessage: null,
 }
 }

 let replay: ManualOfflineSyncOutcome['replay'] = emptyReplay()
 let replayThrewMessage: string | null = null
 try {
 const { replayQueue } = await import('slices/offlineMiddleware')
 replay = await replayQueue()
 } catch (e) {
 replayThrewMessage = errorMessage(e)
 }

 let posPush: ManualOfflineSyncOutcome['posPush'] = { pushed: 0, errors: [] }
 let posPushThrewMessage: string | null = null
 try {
 const { flushTableOps } = await import('services/TableSyncService')
 await flushTableOps()
 } catch (e) {
 console.warn('Table ops manual flush failed:', e)
 }
 try {
 const SyncService = await import('services/SyncService')
 posPush = await SyncService.pushPending()
 } catch (e) {
 posPushThrewMessage = errorMessage(e)
 posPush = { pushed: 0, errors: [posPushThrewMessage] }
 }

 const bootstrap = await runOfflineBootstrap({ force: true, reason: 'manual-sync' })
 return {
 bootstrap,
 replay,
 posPush,
 skippedOffline: false,
 replayThrewMessage,
 posPushThrewMessage,
 }
}
